<template>
  <div class="pdf-form-viewer" v-if="hasFile">
    <!-- Panel lateral de campos a completar (opción C): lista navegable que
         resalta/salta al campo en el PDF y marca su estado de completado. -->
    <aside
      v-if="showFieldList && loaded && fields.length"
      class="pdf-form-viewer__sidebar"
    >
      <div class="pdf-form-viewer__sidebar-title">
        Campos a completar
        <span class="pdf-form-viewer__sidebar-count" :class="{ 'is-ok': invalidCount === 0 }">
          {{ fields.length - invalidCount }}/{{ fields.length }}
        </span>
      </div>

      <!-- Agrupado por página: cada grupo muestra su estado y cuántos campos
           están completos, para que el usuario sepa a qué páginas ir. -->
      <div
        v-for="group in fieldsByPage"
        :key="group.page"
        class="pdf-form-viewer__page-group"
      >
        <div
          class="pdf-form-viewer__page-head"
          :class="{
            'is-current': group.page === actualPage,
            'is-ok': group.pending === 0,
            'has-pending': group.pending > 0,
          }"
          @click="goToPage(group.page)"
        >
          <span class="pdf-form-viewer__page-dot">
            <feather-icon
              :icon="group.pending === 0 ? 'CheckCircleIcon' : 'AlertCircleIcon'"
              size="14"
            />
          </span>
          <span class="pdf-form-viewer__page-title">Página {{ group.page }}</span>
          <span
            class="pdf-form-viewer__page-count"
            :class="{ 'is-ok': group.pending === 0 }"
          >
            {{ group.done }}/{{ group.fields.length }}
          </span>
        </div>

        <ul class="pdf-form-viewer__sidebar-list">
          <li
            v-for="field in group.fields"
            :key="field.id"
            class="pdf-form-viewer__sidebar-item"
            :class="{
              'is-done': isFieldComplete(field),
              'is-active': field.id === highlightedId,
            }"
            @click="goToField(field)"
          >
            <span class="pdf-form-viewer__sidebar-check">
              <feather-icon
                :icon="isFieldComplete(field) ? 'CheckCircleIcon' : 'CircleIcon'"
                size="14"
              />
            </span>
            <span class="pdf-form-viewer__sidebar-name" :title="field.label">
              {{ field.label }}<span v-if="field.required" class="pdf-form-viewer__req">*</span>
            </span>
          </li>
        </ul>
      </div>
    </aside>

    <div class="pdf-form-viewer__main">
    <div class="pdf-form-viewer__toolbar">
      <PdfViewportTools
        v-if="pages && loaded"
        :pages="pages"
        v-model="page"
      />
      <div class="pdf-form-viewer__status" v-if="loaded">
        <span
          class="pdf-form-viewer__missing"
          :class="{ 'is-ok': invalidCount === 0 }"
        >
          <template v-if="invalidCount === 0">Completo</template>
          <template v-else>{{ invalidCount }} campo(s) con error</template>
        </span>
        <div class="pdf-form-viewer__zoom">
          <b-button size="sm" variant="outline-primary" @click="zoomOut">
            <feather-icon icon="ZoomOutIcon" />
          </b-button>
          <span class="pdf-form-viewer__zoom-label">{{ zoomPercent }}%</span>
          <b-button size="sm" variant="outline-primary" @click="zoomIn">
            <feather-icon icon="ZoomInIcon" />
          </b-button>
          <b-button size="sm" variant="outline-secondary" @click="resetZoom">
            100%
          </b-button>
        </div>
      </div>
    </div>

    <div ref="scroll" class="pdf-form-viewer__scroll">
      <div class="pdf-form-viewer__zoom-frame" :style="zoomFrameStyle">
        <div ref="page" class="pdf-form-viewer__page" :style="pageStyle">
          <Pdf
            class="pdf-form-viewer__canvas"
            :page="actualPage"
            :src="file"
            ref="pdf"
            @num-pages="onNumPages"
            @page-loaded="onPageLoaded"
            @page-size="onPageSize"
          />
          <div class="pdf-form-viewer__overlay">
            <div
              v-for="field in fieldsOnPage"
              :key="field.id"
              :ref="`field-${field.id}`"
              class="pdf-form-viewer__field"
              :class="{ 'is-highlighted': field.id === highlightedId }"
              :style="fieldStyle(field)"
            >
              <span
                v-if="field.label"
                class="pdf-form-viewer__field-label"
                :title="field.label"
              >
                {{ field.label }}<span v-if="field.required" class="pdf-form-viewer__req">*</span>
              </span>
              <FieldInput
                :field="field"
                :value="values[valueKey(field)]"
                :font-size-px="fontSizePxFor(field)"
                :error="showErrors ? fieldError(field) : null"
                @input="setValue(field, $event)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>

    <b-overlay :show="!loaded" no-wrap />
  </div>
</template>

<script>

import Pdf from 'vue-pdf-signature';

import PdfViewportTools from '@/febos/global/pdf/components/PdfViewportTools';
import FieldInput from '@/febos/global/pdf/formBuilder/components/FieldInput';
import PdfCoordinateService from '@/febos/global/pdf/formBuilder/services/PdfCoordinateService';
import { ioDownloadPrivateFile } from '@/febos/servicios/api/herramientas.api';
import { validateFieldValue, isEmptyValue, valueKeyOf } from '@/febos/global/pdf/formBuilder/model/fieldModel';

const ZOOM_STEP = 0.25;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;

/**
 * PdfFormViewer — modo "completar" (SPEC Fase 4). Toma una plantilla de campos
 * (fields, en puntos PDF) y renderiza un input por campo sobre el PDF para que
 * un usuario final ingrese valores. NO permite mover/editar campos.
 *
 * Los valores se guardan APARTE de los campos (SPEC §38): v-model = objeto
 * `values` indexado por field.id. La plantilla no se modifica.
 */
export default {
  name: 'PdfFormViewer',
  components: {
    Pdf,
    PdfViewportTools,
    FieldInput,
  },
  model: {
    prop: 'values',
    event: 'input',
  },
  props: {
    fields: { type: Array, default: () => [] },
    values: { type: Object, default: () => ({}) },
    s3url: { type: String, default: undefined },
    path: { type: String, default: undefined },
    name: { type: String, default: 'documento.pdf' },
    defaultPage: { type: Number, default: 1 },
    // Cuando true, resalta los requeridos sin completar.
    showErrors: { type: Boolean, default: true },
    // Carga diferida: el visor solo abre el PDF cuando se activa (evita que dos
    // instancias de vue-pdf-signature compitan por el worker PDF.js singleton).
    active: { type: Boolean, default: true },
    // Muestra el panel lateral con la lista de campos a completar (opción C).
    showFieldList: { type: Boolean, default: true },
  },
  data() {
    return {
      file: undefined,
      pdfDocument: undefined,
      pages: undefined,
      page: this.defaultPage,
      highlightedId: null,
      loaded: false,
      publicandoFile: false,
      zoom: 1,
      pageSizes: {},
      coord: new PdfCoordinateService({ pageWidthPt: 0, pageHeightPt: 0 }),
    };
  },
  computed: {
    hasFile() {
      return !!(this.s3url || this.path);
    },
    actualPage() {
      return this.page === -1 ? this.pages : this.page;
    },
    zoomPercent() {
      return Math.round(this.zoom * 100);
    },
    currentPageSize() {
      return this.pageSizes[this.actualPage];
    },
    pageStyle() {
      const size = this.currentPageSize;
      if (!size) {
        return {};
      }
      const style = {
        width: `${size.width}px`,
        height: `${size.height}px`,
      };
      if (this.zoom !== 1) {
        style.transform = `scale(${this.zoom})`;
        style.transformOrigin = 'top left';
      }
      return style;
    },
    zoomFrameStyle() {
      const size = this.currentPageSize;
      if (!size) {
        return {};
      }
      return {
        width: `${size.width * this.zoom}px`,
        height: `${size.height * this.zoom}px`,
      };
    },
    fieldsOnPage() {
      return this.fields.filter((f) => f.page === this.actualPage);
    },
    // Cantidad de campos con error (requeridos vacíos o formato inválido).
    invalidCount() {
      return this.fields.filter((f) => this.fieldError(f) != null).length;
    },
    // Campos agrupados por página, con resumen de completados/pendientes y
    // requeridos, para el panel lateral de navegación.
    fieldsByPage() {
      const groups = {};
      this.fields.forEach((f) => {
        const page = f.page || 1;
        if (!groups[page]) {
          groups[page] = {
            page, fields: [], done: 0, pending: 0, required: 0,
          };
        }
        groups[page].fields.push(f);
        if (this.isFieldComplete(f)) {
          groups[page].done += 1;
        }
        // "pending" = campos que impiden enviar (requerido vacío o con error).
        if (this.fieldError(f) != null) {
          groups[page].pending += 1;
        }
        if (f.required) {
          groups[page].required += 1;
        }
      });
      return Object.values(groups).sort((a, b) => a.page - b.page);
    },
  },
  watch: {
    s3url() { if (this.active) { this.loadFile(); } },
    path() { if (this.active) { this.loadFile(); } },
    actualPage() { this.syncCoordForPage(); },
    // Solo aplicar defaults en modo completar (active). En el editor, los
    // cambios a `fields` (p.ej. tipear el valor por defecto) NO deben tocar
    // `values`, o se congelaría con el primer carácter tipeado.
    fields() { if (this.active) { this.applyDefaults(); } },
    active(now) {
      // Primera activación: cargar el PDF si aún no se cargó.
      if (now && !this.file) {
        this.loadFile();
      }
      if (now) {
        this.applyDefaults();
      }
    },
  },
  methods: {
    async getS3Url() {
      if (this.s3url) {
        return this.s3url;
      }
      if (!this.path) {
        return '';
      }
      if (this.path.startsWith('http')) {
        return this.path;
      }
      this.publicandoFile = true;
      const { data: { url } } = await ioDownloadPrivateFile({
        path: this.path,
        nombre: this.name,
        descargable: false,
      });
      this.publicandoFile = false;
      return url;
    },
    async loadFile() {
      this.clear();
      const url = await this.getS3Url();
      if (!url) {
        return;
      }
      // IMPORTANTE: no consumir task.promise aquí. El <Pdf> de vue-pdf-signature
      // debe ser el único consumidor de la loading task; si se await-ea antes,
      // al montar el <Pdf> el documento ya está resuelto y no re-renderiza la
      // página (no emite page-size/page-loaded). El número de páginas llega por
      // el evento @num-pages (onNumPages).
      this.file = Pdf.createLoadingTask(url);
    },
    clear() {
      this.file = undefined;
      this.pages = undefined;
      this.page = this.defaultPage;
      this.loaded = false;
      this.pageSizes = {};
    },
    onNumPages(n) {
      if (n) {
        this.pages = n;
      }
    },
    onPageLoaded() {
      if (!this.loaded) {
        this.loaded = true;
        this.$emit('loaded');
      }
    },
    onPageSize(widthPt, heightPt) {
      this.$set(this.pageSizes, this.actualPage, { width: widthPt, height: heightPt });
      this.syncCoordForPage();
    },
    syncCoordForPage() {
      const size = this.currentPageSize;
      if (!size) {
        return;
      }
      this.coord.setPageSize(size.width, size.height);
      this.coord.setRenderedWidth(size.width);
    },
    fieldStyle(field) {
      const r = this.coord.pdfRectToScreen({
        x: field.x, y: field.y, width: field.width, height: field.height,
      });
      return {
        position: 'absolute',
        left: `${r.x}px`,
        top: `${r.y}px`,
        width: `${r.width}px`,
        height: `${r.height}px`,
      };
    },
    // fontSize configurado (puntos PDF) escalado a px pantalla, con la misma
    // relación px/pt del campo pintado. Null si no hay fontSize configurado.
    fontSizePxFor(field) {
      const sizePt = field.style && field.style.fontSize;
      if (!sizePt || !field.height) {
        return null;
      }
      const r = this.coord.pdfRectToScreen({
        x: field.x, y: field.y, width: field.width, height: field.height,
      });
      const pxPerPt = r.height / field.height;
      return sizePt * pxPerPt;
    },
    // Clave con la que se indexa el valor del campo (linkKey o id). Campos con
    // la misma clave comparten valor.
    valueKey(field) {
      return valueKeyOf(field);
    },
    // Error del campo según su tipo y reglas (requerido + formato, p.ej. RUT).
    // Devuelve el mensaje de error o null si es válido.
    fieldError(field) {
      return validateFieldValue(field, this.values[this.valueKey(field)]).error;
    },
    // ¿El campo está correctamente completado? (sin error de validación).
    isFieldComplete(field) {
      const v = this.values[this.valueKey(field)];
      return validateFieldValue(field, v).valid && !isEmptyValue(field.type, v);
    },
    // Panel lateral: cambia a la página indicada.
    goToPage(page) {
      if (page !== this.actualPage) {
        this.page = page;
      }
    },
    // Panel lateral: salta a la página del campo, lo resalta y hace scroll a él.
    goToField(field) {
      if (field.page !== this.actualPage) {
        this.page = field.page;
      }
      this.highlightedId = field.id;
      this.$nextTick(() => {
        const ref = this.$refs[`field-${field.id}`];
        const el = Array.isArray(ref) ? ref[0] : ref;
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        }
      });
    },
    setValue(field, value) {
      // Se escribe por la clave de valor: si varios campos comparten linkKey,
      // todos quedan sincronizados automáticamente.
      const key = this.valueKey(field);
      this.$emit('input', { ...this.values, [key]: value });
      this.$emit('value-change', { fieldId: field.id, key, value });
    },
    // Pre-rellena los valores con el defaultValue de cada campo que lo tenga y
    // que aún no tenga un valor ingresado. No pisa lo que el usuario ya escribió.
    applyDefaults() {
      const next = { ...this.values };
      let changed = false;
      this.fields.forEach((f) => {
        const key = this.valueKey(f);
        const sinValor = next[key] == null || next[key] === '';
        if (sinValor && f.defaultValue != null && f.defaultValue !== '') {
          next[key] = f.defaultValue;
          changed = true;
        }
      });
      if (changed) {
        this.$emit('input', next);
      }
    },
    zoomIn() {
      this.zoom = Math.min(ZOOM_MAX, +(this.zoom + ZOOM_STEP).toFixed(2));
    },
    zoomOut() {
      this.zoom = Math.max(ZOOM_MIN, +(this.zoom - ZOOM_STEP).toFixed(2));
    },
    resetZoom() {
      this.zoom = 1;
    },
  },
  created() {
    // Carga solo si arranca activo; si no, espera al watcher de `active`.
    if (this.active) {
      this.loadFile();
      this.applyDefaults();
    }
  },
};

</script>

<style lang="scss" scoped>

@import '~@core/scss/base/bootstrap-extended/_include';

.pdf-form-viewer {
  display: flex;
  flex-direction: row;
  flex: 1;
  background-color: $gray-100;
  position: relative;
  overflow: hidden;
}

.pdf-form-viewer__main {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

// Panel lateral de campos (opción C).
.pdf-form-viewer__sidebar {
  width: 220px;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-right: 1px solid #e3e3ef;
  overflow: auto;
}

.pdf-form-viewer__sidebar-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 700;
  color: #30304d;
  border-bottom: 1px solid #eee;
}

.pdf-form-viewer__sidebar-count {
  font-size: 11px;
  font-weight: 700;
  color: #ea5455;
  background: #fdeaea;
  border-radius: 10px;
  padding: 1px 8px;
}

.pdf-form-viewer__sidebar-count.is-ok {
  color: #28c76f;
  background: #e3f8ee;
}

.pdf-form-viewer__page-group {
  border-bottom: 1px solid #f0f0f5;
}

.pdf-form-viewer__page-head {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: #30304d;
}

.pdf-form-viewer__page-head:hover {
  background: #f6f6fb;
}

.pdf-form-viewer__page-head.is-current {
  background: #f3f2fe;
  box-shadow: inset 3px 0 0 #7367f0;
}

.pdf-form-viewer__page-dot {
  display: flex;
  color: #ea5455;
}

.pdf-form-viewer__page-head.is-ok .pdf-form-viewer__page-dot {
  color: #28c76f;
}

.pdf-form-viewer__page-title {
  flex: 1;
}

.pdf-form-viewer__page-count {
  font-size: 11px;
  font-weight: 700;
  color: #ea5455;
  background: #fdeaea;
  border-radius: 10px;
  padding: 0 7px;
}

.pdf-form-viewer__page-count.is-ok {
  color: #28c76f;
  background: #e3f8ee;
}

.pdf-form-viewer__sidebar-list {
  list-style: none;
  margin: 0;
  padding: 2px 6px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.pdf-form-viewer__sidebar-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  color: #30304d;
}

.pdf-form-viewer__sidebar-item:hover {
  background: #f3f2fe;
}

.pdf-form-viewer__sidebar-item.is-active {
  background: #ede9fe;
  box-shadow: inset 0 0 0 1px #7367f0;
}

.pdf-form-viewer__sidebar-check {
  color: #c3c3d6;
  display: flex;
}

.pdf-form-viewer__sidebar-item.is-done .pdf-form-viewer__sidebar-check {
  color: #28c76f;
}

.pdf-form-viewer__sidebar-name {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pdf-form-viewer__sidebar-page {
  font-size: 10px;
  color: #9a9ab0;
}

.pdf-form-viewer__toolbar {
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: center;
  padding: map-get($spacers, 1);
  gap: map-get($spacers, 2);
  overflow-x: auto;
}

.pdf-form-viewer__status {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: map-get($spacers, 2);
  flex-shrink: 0;
}

.pdf-form-viewer__missing {
  font-size: 0.8rem;
  font-weight: 600;
  color: #ea5455;
  white-space: nowrap;
}

.pdf-form-viewer__missing.is-ok {
  color: #28c76f;
}

.pdf-form-viewer__zoom {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: map-get($spacers, 1);
  flex-shrink: 0;
}

.pdf-form-viewer__zoom-label {
  min-width: 42px;
  text-align: center;
  font-size: 0.85rem;
}

.pdf-form-viewer__scroll {
  flex: 1;
  overflow: auto;
  padding: map-get($spacers, 2);
  // Contenedor flex: centra el documento cuando cabe y, cuando el PDF (con
  // zoom) es más ancho que el área, el patrón flex + margin:auto mantiene
  // AMBOS bordes accesibles al hacer scroll horizontal (a diferencia de un
  // `margin:0 auto` en bloque, que recorta el borde izquierdo).
  display: flex;
  // Alinear arriba evita que el borde superior se recorte cuando el PDF es
  // más alto que el área visible (el scroll vertical llega hasta el tope).
  align-items: flex-start;
}

.pdf-form-viewer__zoom-frame {
  // No encoge: conserva su ancho real (size * zoom) para que el contenedor
  // haga scroll en lugar de comprimir el documento.
  flex: 0 0 auto;
  // Centrado horizontal con auto-márgenes dentro del flex: respeta el overflow
  // en AMBOS lados (a diferencia de `margin:0 auto` en bloque, que recorta el
  // borde izquierdo cuando el contenido es más ancho que el contenedor).
  margin-left: auto;
  margin-right: auto;
}

.pdf-form-viewer__page {
  position: relative;
  margin: 0 auto;
}

.pdf-form-viewer__canvas {
  display: block;
  width: 100%;
  height: 100%;
}

.pdf-form-viewer__overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.pdf-form-viewer__field {
  position: absolute;
}

// Etiqueta del campo (opción B): texto pequeño encima de la caja, con fondo
// claro para que sea legible sobre el documento.
.pdf-form-viewer__field-label {
  position: absolute;
  bottom: 100%;
  left: 0;
  max-width: 100%;
  margin-bottom: 1px;
  padding: 0 3px;
  font-size: 9px;
  line-height: 1.3;
  color: #4b4b6b;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
}

.pdf-form-viewer__req {
  color: #ea5455;
  margin-left: 1px;
}

// Resaltado cuando se selecciona el campo desde el panel lateral (opción C).
.pdf-form-viewer__field.is-highlighted {
  outline: 2px solid #7367f0;
  outline-offset: 1px;
  border-radius: 2px;
}

</style>
